import React from 'react';
import styles from './Portfolios.module.css';
import { PrimaryButton, DefaultButton } from '@fluentui/react';
import { useNavigate } from 'react-router-dom';
import { Notification } from '../Components/Notification';
import { CreatePortfolioModal } from '../Components/CreatePortfolioModal';
import { StandardTable } from '../Components/StandardTable';
import { Portfolio } from '../Services/auth-service';
import { apiClient } from '../Services/api-client';

export const Portfolios: React.FC = () => {
  const navigate = useNavigate();
  const [portfolios, setPortfolios] = React.useState<Portfolio[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [isCreateOpen, setIsCreateOpen] = React.useState(false);
  const [editingPortfolio, setEditingPortfolio] = React.useState<Portfolio | null>(null);
  const [notification, setNotification] = React.useState<{
    type: 'success' | 'error';
    message: string;
  } | null>(null);

  const loadPortfolios = React.useCallback(async () => {
    setLoading(true);
    try {
      const response = await apiClient.get('/portfolios');
      setPortfolios(response.data || []);
    } catch (error) {
      setNotification({
        type: 'error',
        message: 'Failed to load portfolios',
      });
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    loadPortfolios();
  }, [loadPortfolios]);


  return (
    <div className={styles.pageContainer}>
      <div className={styles.header}>
        <h1>Portfolios</h1>
        <p className={styles.subtitle}>Manage PMS Provider portfolios</p>
      </div>

      {notification && (
        <Notification
          type={notification.type}
          message={notification.message}
          onDismiss={() => setNotification(null)}
        />
      )}

      <div className={styles.toolbar}>
        <PrimaryButton
          iconProps={{ iconName: 'Add' }}
          text="Create Portfolio"
          onClick={() => {
            setEditingPortfolio(null);
            setIsCreateOpen(true);
          }}
        />
      </div>
      <StandardTable
        items={portfolios}
        totalCount={portfolios.length}
        loading={loading}
        filtersSummary="None"
        onClearFilters={() => undefined}
        onRefresh={loadPortfolios}
        emptyState={{
          isFiltered: false,
          emptyMessage: 'No portfolios yet. Create one to get started.',
          filteredMessage: 'No results. Clear filters.',
        }}
        renderTable={(paged) => (
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Portfolio Name</th>
                <th>API Host</th>
                <th>Contact</th>
                <th>Status</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {paged.map((portfolio) => (
                <tr key={portfolio.portfolioId}>
                  <td>
                    <strong>{portfolio.friendlyName}</strong>
                  </td>
                  <td className={styles.monospace}>{portfolio.apiHost}</td>
                  <td>
                    <div>{portfolio.contact?.name || '-'}</div>
                    <div className={styles.secondary}>{portfolio.contact?.email || ''}</div>
                  </td>
                  <td>
                    <span className={`${styles.badge} ${styles.active}`}>
                      {portfolio.status}
                    </span>
                  </td>
                  <td className={styles.secondary}>
                    {new Date(portfolio.createdAt).toLocaleDateString()}
                  </td>
                  <td>
                    <div className={styles.rowActions}>
                    <DefaultButton
                      text="Communities"
                      onClick={() =>
                        navigate(`/portfolios/${portfolio.portfolioId}/communities`)
                      }
                    />
                    <DefaultButton
                      text="Edit"
                      onClick={() => {
                        setEditingPortfolio(portfolio);
                        setIsCreateOpen(true);
                      }}
                    />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      />

      <CreatePortfolioModal
        isOpen={isCreateOpen}
        existingPortfolio={editingPortfolio || undefined}
        onDismiss={() => {
          setIsCreateOpen(false);
          setEditingPortfolio(null);
        }}
        onSaved={(saved, isEdit) => {
          setNotification({
            type: 'success',
            message: isEdit ? 'Portfolio updated successfully' : 'Portfolio created successfully',
          });
          if (isEdit) {
            setPortfolios((prev) =>
              prev.map((portfolio) =>
                portfolio.portfolioId === saved.portfolioId ? saved : portfolio
              )
            );
          } else {
            setPortfolios((prev) => [saved, ...prev]);
          }
        }}
        onError={(message) =>
          setNotification({
            type: 'error',
            message,
          })
        }
      />
    </div>
  );
};
